import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { VaultShelf } from '@/types/api';
import { humanizeApiError } from '@/lib/humanizeApiError';

/**
 * Pure list transforms for the vault-shelves list. Extracted from the hook so
 * the optimistic-update logic is unit-testable without React or the network.
 *
 * Shelves are tags, not folders — order is the server's (creation order); we
 * never re-sort, so a rename keeps a shelf in place and a create appends.
 */

/** Replace a shelf by id, or append it when it's new (server echo after create). */
export function upsertShelf(list: VaultShelf[], shelf: VaultShelf): VaultShelf[] {
  const i = list.findIndex((s) => s.id === shelf.id);
  if (i === -1) return [...list, shelf];
  const next = list.slice();
  next[i] = shelf;
  return next;
}

/** Rename a shelf in place (no reorder). No-op if the id is unknown. */
export function renameInList(list: VaultShelf[], id: string, name: string): VaultShelf[] {
  return list.map((s) => (s.id === id ? { ...s, name } : s));
}

/** Drop a shelf by id. */
export function removeFromList(list: VaultShelf[], id: string): VaultShelf[] {
  return list.filter((s) => s.id !== id);
}

/**
 * Vault shelves ("полки") — the user's tag-style collections inside the Сейф.
 * Mirrors the mobile hook: optimistic CRUD reconciled against the server echo
 * (and a reload on failure so the UI never lies).
 *
 * `enabled` gates the fetch: callers that mount the hook permanently (e.g. a
 * header button whose popover is usually closed) pass the open flag so we only
 * hit the network when the UI is actually shown — and re-fetch on each open, so
 * counts stay fresh after a sibling view (the shelf picker) mutates them.
 */
/**
 * @param onError optional callback the host surfaces (a toast) when an
 *   optimistic create/rename/remove is rejected by the server. The list is
 *   still reconciled via reload; this just stops the failure being silent.
 */
export function useVaultShelves(enabled = true, onError?: (err: unknown) => void) {
  const [shelves, setShelves] = useState<VaultShelf[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id — only the latest in-flight reload may write state, so
  // a slow earlier response can't clobber a fresher one.
  const reqId = useRef(0);

  const reload = useCallback(async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.listShelves();
      if (reqId.current !== myReq) return;
      // Never trust the payload into state as-is: a malformed/mock response
      // without `shelves` must not become `undefined` (the rail reads `.length`).
      setShelves(Array.isArray(res?.shelves) ? res.shelves : []);
    } catch (err) {
      if (reqId.current !== myReq) return;
      setError(humanizeApiError(err, 'failed').title);
    } finally {
      if (reqId.current === myReq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const create = useCallback(async (name: string): Promise<VaultShelf | null> => {
    try {
      const shelf = await api.createShelf({ name: name.trim() });
      setShelves((prev) => upsertShelf(prev, shelf));
      return shelf;
    } catch (err) {
      void reload();
      onError?.(err);
      return null;
    }
  }, [reload, onError]);

  const rename = useCallback(async (id: string, name: string) => {
    setShelves((prev) => renameInList(prev, id, name.trim()));
    try {
      const shelf = await api.renameShelf(id, { name: name.trim() });
      setShelves((prev) => upsertShelf(prev, shelf));
    } catch (err) {
      void reload();
      onError?.(err);
    }
  }, [reload, onError]);

  const remove = useCallback(async (id: string) => {
    setShelves((prev) => removeFromList(prev, id));
    try {
      await api.deleteShelf(id);
    } catch (err) {
      void reload();
      onError?.(err);
    }
  }, [reload, onError]);

  return { shelves, loading, error, reload, create, rename, remove };
}
